import React, { useState } from 'react';
import MemberFamilyDialog from './MemberFamilyDialog';
import DateOfBirthSelect from '../../ui/DateOfBirthSelect';
import { Gender, ShirtSize } from '../../../types';
import { toTitleCase } from '../../../utils/textNormalization';
import type { DashboardOwnMember } from './types';
import {
  previewFamilyChange,
  submitFamilyChangeRequest,
  type FamilyChangeKind,
  type PreviewResult,
} from './familyChangeClient';
import PaymentChoice from '../../payment/PaymentChoice';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

type Choice = 'NEW_PERSON' | 'EXISTING_MEMBER' | null;

interface AddFamilyMemberDialogProps {
  own: DashboardOwnMember;
  onClose: () => void;
  /** Called once the request has been submitted (payment resolved or not needed). */
  onDone: (message: string) => void;
}

interface NewPersonForm {
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  gender: Gender;
  shirtSize: ShirtSize;
  email: string;
  phone: string;
  wantsPortalAccess: boolean;
}

/**
 * "Add a family member" (R6, design §7.2–§7.3): the family primary or an
 * individual chooses New person or Someone who is already a member, sees a
 * server-computed preview, submits, then — only when the amount owed is
 * above $0 — pays with `PaymentChoice` (online now or at the desk).
 */
const AddFamilyMemberDialog: React.FC<AddFamilyMemberDialogProps> = ({ own, onClose, onDone }) => {
  const [choice, setChoice] = useState<Choice>(null);

  const [newPerson, setNewPerson] = useState<NewPersonForm>({
    firstName: '',
    lastName: '',
    dateOfBirth: '',
    gender: Gender.PREFER_NOT_TO_SAY,
    shirtSize: ShirtSize.M,
    email: '',
    phone: '',
    wantsPortalAccess: false,
  });
  const [memberNumber, setMemberNumber] = useState('');
  const [existingDob, setExistingDob] = useState('');

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [preview, setPreview] = useState<PreviewResult | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const [requestId, setRequestId] = useState<string | null>(null);
  const [submittedMessage, setSubmittedMessage] = useState<string | null>(null);

  const resetFormState = () => {
    setPreview(null);
    setServerError(null);
    setErrors({});
  };

  const selectChoice = (next: Exclude<Choice, null>) => {
    setChoice(next);
    resetFormState();
  };

  const buildNewPersonPayload = () => ({
    person: {
      firstName: newPerson.firstName.trim(),
      lastName: newPerson.lastName.trim(),
      dateOfBirth: newPerson.dateOfBirth,
      gender: newPerson.gender,
      shirtSize: newPerson.shirtSize,
      email: newPerson.email.trim() || undefined,
      phone: newPerson.phone.trim() || undefined,
      wantsPortalAccess: newPerson.wantsPortalAccess,
    },
  });

  const validateNewPerson = (): boolean => {
    const next: Record<string, string> = {};
    if (!newPerson.firstName.trim()) next.firstName = 'First name is required';
    if (!newPerson.lastName.trim()) next.lastName = 'Last name is required';
    if (!newPerson.dateOfBirth) next.dateOfBirth = 'Date of birth is required';
    if (newPerson.wantsPortalAccess) {
      if (!newPerson.email.trim()) next.email = 'Email is required for a login';
      else if (!EMAIL_REGEX.test(newPerson.email.trim())) next.email = 'Please enter a valid email address';
    } else if (newPerson.email.trim() && !EMAIL_REGEX.test(newPerson.email.trim())) {
      next.email = 'Please enter a valid email address';
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const validateExistingMember = (): boolean => {
    const next: Record<string, string> = {};
    if (!memberNumber.trim()) next.memberNumber = 'Member number is required';
    if (!existingDob) next.dateOfBirth = 'Date of birth is required';
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handlePreview = async () => {
    if (choice === 'NEW_PERSON' && !validateNewPerson()) return;
    if (choice === 'EXISTING_MEMBER' && !validateExistingMember()) return;

    setPreviewing(true);
    setServerError(null);
    try {
      const kind: FamilyChangeKind = choice === 'NEW_PERSON' ? 'NEW_PERSON' : 'EXISTING_MEMBER';
      const payload =
        choice === 'NEW_PERSON'
          ? buildNewPersonPayload()
          : { memberNumber: memberNumber.trim(), dateOfBirth: existingDob };
      const result = await previewFamilyChange(kind, payload);
      if (result.success) {
        setPreview(result);
      } else {
        setServerError(result.message || 'Could not preview this request. Please try again.');
      }
    } catch (err: any) {
      setServerError(err?.message || 'Could not preview this request. Please try again.');
    } finally {
      setPreviewing(false);
    }
  };

  const handleSubmit = async () => {
    if (!choice) return;
    setSubmitting(true);
    setServerError(null);
    try {
      const kind: FamilyChangeKind = choice === 'NEW_PERSON' ? 'NEW_PERSON' : 'EXISTING_MEMBER';
      const payload =
        choice === 'NEW_PERSON'
          ? buildNewPersonPayload()
          : { memberNumber: memberNumber.trim(), dateOfBirth: existingDob };
      const result = await submitFamilyChangeRequest(kind, payload);
      if (result.success) {
        if (choice === 'EXISTING_MEMBER') {
          // R6.8: never reveal match status to the requester.
          onDone("We'll check this and let you know.");
          return;
        }
        const amountDueCents = preview?.amountDueCents ?? 0;
        if (amountDueCents > 0 && result.requestId) {
          setRequestId(result.requestId);
          setSubmittedMessage(result.message || null);
        } else {
          onDone(result.message || 'Request submitted.');
        }
      } else {
        setServerError(result.message || 'Could not submit this request. Please try again.');
      }
    } catch (err: any) {
      setServerError(err?.message || 'Could not submit this request. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls = (hasError: boolean) =>
    `min-h-[44px] w-full rounded-lg border px-3 py-2 focus:outline-none focus:ring-2 focus:ring-fishing-blue ${
      hasError ? 'border-red-500' : 'border-gray-300'
    }`;

  // Payment step: amount above $0 for a NEW_PERSON request.
  if (requestId) {
    const amountCents = preview?.amountDueCents ?? 0;
    return (
      <MemberFamilyDialog
        titleId="afm-pay-title"
        title="Pay for this family member"
        descriptionId="afm-pay-desc"
        widthClassName="max-w-lg"
        onClose={() => onDone(submittedMessage || 'Request submitted.')}
      >
        <p id="afm-pay-desc" className="mt-1 text-sm text-gray-600">
          {preview?.message}
        </p>
        <div className="mt-4">
          <PaymentChoice
            amountCents={amountCents}
            amountDisplay={`$${(amountCents / 100).toFixed(2)}`}
            paymentType="FAMILY_MEMBER_ADD"
            entityId={requestId}
            entityType="FAMILY_CHANGE_REQUEST"
            payerEmail={own.email ?? ''}
            payerName={`${own.firstName} ${own.lastName}`}
            feeKey="family_member_add"
            authMode="userPool"
            onSuccess={() => onDone('Payment received. Your request is waiting for the committee.')}
            onPayOnDay={() => onDone('Your request is waiting for the committee. Pay at the desk when you come in.')}
          />
        </div>
      </MemberFamilyDialog>
    );
  }

  return (
    <MemberFamilyDialog
      titleId="afm-title"
      title="Add a family member"
      descriptionId="afm-desc"
      busy={submitting || previewing}
      onClose={onClose}
      widthClassName="max-w-lg"
    >
      <p id="afm-desc" className="mt-1 text-sm text-gray-600">
        Add a new person, or someone who is already a member. The committee will review your
        request before it takes effect.
      </p>

      {serverError && (
        <p role="alert" className="mt-3 rounded-md border border-red-200 bg-red-50 p-2 text-sm font-medium text-red-700">
          {serverError}
        </p>
      )}

      {choice === null && (
        <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
          <button
            type="button"
            onClick={() => selectChoice('NEW_PERSON')}
            className="min-h-[44px] rounded-lg border-2 border-fishing-blue p-4 text-left text-sm font-medium text-fishing-blue hover:bg-blue-50"
          >
            <div className="font-bold">New person</div>
            <div className="text-gray-600">Enter their details</div>
          </button>
          <button
            type="button"
            onClick={() => selectChoice('EXISTING_MEMBER')}
            className="min-h-[44px] rounded-lg border-2 border-gray-300 p-4 text-left text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            <div className="font-bold">Someone who is already a member</div>
            <div className="text-gray-600">By member number and date of birth</div>
          </button>
        </div>
      )}

      {choice === 'NEW_PERSON' && (
        <div className="mt-4 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label htmlFor="afm-first-name" className="block text-sm font-medium text-gray-700 mb-1">
                First name *
              </label>
              <input
                id="afm-first-name"
                type="text"
                value={newPerson.firstName}
                onChange={(e) => {
                  setNewPerson((p) => ({ ...p, firstName: toTitleCase(e.target.value) }));
                  setPreview(null);
                }}
                className={inputCls(!!errors.firstName)}
              />
              {errors.firstName && <p className="mt-1 text-sm text-red-600">{errors.firstName}</p>}
            </div>
            <div>
              <label htmlFor="afm-last-name" className="block text-sm font-medium text-gray-700 mb-1">
                Last name *
              </label>
              <input
                id="afm-last-name"
                type="text"
                value={newPerson.lastName}
                onChange={(e) => {
                  setNewPerson((p) => ({ ...p, lastName: toTitleCase(e.target.value) }));
                  setPreview(null);
                }}
                className={inputCls(!!errors.lastName)}
              />
              {errors.lastName && <p className="mt-1 text-sm text-red-600">{errors.lastName}</p>}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Date of birth *</label>
            <DateOfBirthSelect
              value={newPerson.dateOfBirth}
              onChange={(v) => {
                setNewPerson((p) => ({ ...p, dateOfBirth: v }));
                setPreview(null);
              }}
              error={!!errors.dateOfBirth}
              idPrefix="afm-dob"
            />
            {errors.dateOfBirth && <p className="mt-1 text-sm text-red-600">{errors.dateOfBirth}</p>}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label htmlFor="afm-gender" className="block text-sm font-medium text-gray-700 mb-1">
                Gender
              </label>
              <select
                id="afm-gender"
                value={newPerson.gender}
                onChange={(e) => setNewPerson((p) => ({ ...p, gender: e.target.value as Gender }))}
                className="min-h-[44px] w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-fishing-blue"
              >
                <option value={Gender.MALE}>Male</option>
                <option value={Gender.FEMALE}>Female</option>
                <option value={Gender.OTHER}>Other</option>
                <option value={Gender.PREFER_NOT_TO_SAY}>Prefer not to say</option>
              </select>
            </div>
            <div>
              <label htmlFor="afm-shirt" className="block text-sm font-medium text-gray-700 mb-1">
                Shirt size
              </label>
              <select
                id="afm-shirt"
                value={newPerson.shirtSize}
                onChange={(e) => setNewPerson((p) => ({ ...p, shirtSize: e.target.value as ShirtSize }))}
                className="min-h-[44px] w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-fishing-blue"
              >
                {Object.values(ShirtSize).map((size) => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label htmlFor="afm-email" className="block text-sm font-medium text-gray-700 mb-1">
                Email (optional)
              </label>
              <input
                id="afm-email"
                type="email"
                value={newPerson.email}
                onChange={(e) => setNewPerson((p) => ({ ...p, email: e.target.value }))}
                className={inputCls(!!errors.email)}
              />
              {errors.email && <p className="mt-1 text-sm text-red-600">{errors.email}</p>}
            </div>
            <div>
              <label htmlFor="afm-phone" className="block text-sm font-medium text-gray-700 mb-1">
                Phone (optional)
              </label>
              <input
                id="afm-phone"
                type="tel"
                value={newPerson.phone}
                onChange={(e) => setNewPerson((p) => ({ ...p, phone: e.target.value }))}
                className="min-h-[44px] w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-fishing-blue"
              />
            </div>
          </div>

          <label className="flex min-h-[44px] items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={newPerson.wantsPortalAccess}
              onChange={(e) => setNewPerson((p) => ({ ...p, wantsPortalAccess: e.target.checked }))}
              className="h-4 w-4 rounded border-gray-300 text-fishing-blue focus:ring-fishing-blue"
            />
            Wants a login
          </label>
        </div>
      )}

      {choice === 'EXISTING_MEMBER' && (
        <div className="mt-4 space-y-4">
          <div>
            <label htmlFor="afm-member-number" className="block text-sm font-medium text-gray-700 mb-1">
              Member number *
            </label>
            <input
              id="afm-member-number"
              type="text"
              value={memberNumber}
              onChange={(e) => {
                setMemberNumber(e.target.value);
                setPreview(null);
              }}
              className={inputCls(!!errors.memberNumber)}
            />
            {errors.memberNumber && <p className="mt-1 text-sm text-red-600">{errors.memberNumber}</p>}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Their date of birth *</label>
            <DateOfBirthSelect
              value={existingDob}
              onChange={(v) => {
                setExistingDob(v);
                setPreview(null);
              }}
              error={!!errors.dateOfBirth}
              idPrefix="afm-existing-dob"
            />
            {errors.dateOfBirth && <p className="mt-1 text-sm text-red-600">{errors.dateOfBirth}</p>}
          </div>
          <p className="text-sm text-gray-600">
            We will check this and let you know. No payment is due for adding an existing member.
          </p>
        </div>
      )}

      {preview?.message && (
        <p
          aria-live="polite"
          className="mt-4 rounded-lg border border-fishing-green/30 bg-fishing-green/10 p-3 text-sm font-medium text-fishing-green"
        >
          {preview.message}
        </p>
      )}

      {choice !== null && (
        <div className="mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={() => {
              setChoice(null);
              resetFormState();
            }}
            disabled={submitting || previewing}
            className="min-h-[44px] rounded-lg border border-gray-300 px-4 py-2 font-semibold text-gray-800 hover:bg-gray-50 disabled:opacity-50"
          >
            Back
          </button>
          {!preview ? (
            <button
              type="button"
              onClick={handlePreview}
              disabled={previewing}
              className="min-h-[44px] rounded-lg bg-fishing-blue px-4 py-2 font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {previewing ? 'Checking…' : 'Preview'}
            </button>
          ) : (
            <button
              type="button"
              onClick={handleSubmit}
              disabled={submitting}
              className="min-h-[44px] rounded-lg bg-fishing-blue px-4 py-2 font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {submitting ? 'Submitting…' : 'Submit request'}
            </button>
          )}
        </div>
      )}

      {choice === null && (
        <div className="mt-5 flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="min-h-[44px] rounded-lg border border-gray-300 px-4 py-2 font-semibold text-gray-800 hover:bg-gray-50"
          >
            Cancel
          </button>
        </div>
      )}
    </MemberFamilyDialog>
  );
};

export default AddFamilyMemberDialog;
